import {
  Box,
  IconButton,
  styled,
  useTheme,
  useMediaQuery,
  Typography,
  Container,
} from "@mui/material";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

import { useNavigate } from "react-router";
import { getRandomColor } from "../../utilities";
import Ratings from "../common/Ratings";
import { RectangularSkeleton } from "../../pages/home/Skeleton";
import { useState, useMemo, useCallback, memo } from "react";

const StyledArrowButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1,
  backgroundColor: "var(--neutral-white)",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    backgroundColor: "var(--neutral-white)",
  },
  "& .MuiSvgIcon-root": {
    color: "var(--neutral-black)",
    fontSize: "20px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "30px",
    height: "30px",
    "& .MuiSvgIcon-root": {
      fontSize: "16px",
    },
  },
}));

export function NextArrow({ onClick }) {
  return (
    <StyledArrowButton
      onClick={onClick}
      sx={{ right: { xs: "5px", md: "-20px" } }}
    >
      <ArrowForwardIosIcon />
    </StyledArrowButton>
  );
}

export function PrevArrow({ onClick }) {
  return (
    <StyledArrowButton
      onClick={onClick}
      sx={{ left: { xs: "5px", md: "-20px" } }}
    >
      <ArrowBackIosNewIcon />
    </StyledArrowButton>
  );
}

function PopularCakes({ data, isLoading }) {
  let navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ✅ Memoize filledProducts — not recalculated every render
  const filledProducts = useMemo(() => {
    if (!data?.products?.length) return [];
    return data.products.length < 3
      ? [...data.products, ...data.products, ...data.products].slice(0, 3)
      : data.products;
  }, [data?.products]);

  if (!data?.products?.length) return null;

  // ✅ customPaging only used on mobile (dots only show on mobile)
  const customPaging = useCallback(
    (i) => (
      <Box
        sx={{
          width: i === currentSlide ? "16px" : "8px",
          height: "8px",
          borderRadius: i === currentSlide ? "4px" : "50%",
          backgroundColor:
            i === currentSlide
              ? "var(--primary-red-base)"
              : "var(--neutral-300)",
          margin: "0 4px",
          transition: "all 0.5s ease",
          marginTop: "12px",
        }}
      />
    ),
    [currentSlide],
  );

  // ✅ KEY FIX: beforeChange only fires on mobile
  // On desktop, autoplay was calling setCurrentSlide every tick →
  // triggering full re-render of PopularCakes + all cards (caused 81ms render!)
  // On desktop dots are hidden so we don't need to track currentSlide at all
  const settings = useMemo(
    () => ({
      dots: isMobile,
      infinite: true,
      speed: 500,
      autoplay: true,
      slidesToShow: 3,
      slidesToScroll: 1,
      beforeChange: isMobile ? (_, next) => setCurrentSlide(next) : undefined,
      responsive: [
        { breakpoint: 1250, settings: { slidesToShow: 2 } },
        { breakpoint: 900, settings: { slidesToShow: 1.5 } },
        { breakpoint: 768, settings: { slidesToShow: 1.5 } },
        { breakpoint: 480, settings: { slidesToShow: 1 } },
      ],
      customPaging: isMobile ? customPaging : undefined,
    }),
    [isMobile, customPaging],
  );

  return (
    <Section>
      <Container maxWidth="none" className="common-container">
        {isLoading ? (
          <RectangularSkeleton />
        ) : (
          <>
            <SectionHeader>
              <SectionTitle>{data?.headings}</SectionTitle>
            </SectionHeader>
            <SliderWrapper>
              <Slider {...settings}>
                {filledProducts.map((item, index) => (
                  <CakeCardItem
                    key={item?.id || index}
                    item={item}
                    customer={data?.customer}
                  />
                ))}
              </Slider>
            </SliderWrapper>
          </>
        )}
      </Container>
    </Section>
  );
}

// ✅ Wrapped in memo — only re-renders if item or customer actually changes
const CakeCardItem = memo(function CakeCardItem({ item, customer }) {
  const navigate = useNavigate();

  // ✅ Memoize price calculation — heavy logic, no need to rerun every render
  const { discountPrice, originalPrice, discountPercent, comboPrice } =
    useMemo(() => {
      let discountPrice = 0;
      let originalPrice = 0;
      let discountPercent = 0;

      const variation = item?.variation?.[0] || {};
      const bulkPrice = item?.bulk?.bulk_price || [];
      const comboPrice = +item?.combo_price || 0;

      // Priority 1: Combo Price
      if (comboPrice > 0) {
        discountPrice = comboPrice;
        originalPrice =
          +variation.egg > 0 ? +variation.egg : +variation.eggLess;
        if (originalPrice > 0 && originalPrice > comboPrice) {
          discountPercent = Math.round(
            ((originalPrice - comboPrice) / originalPrice) * 100,
          );
        }
      }
      // Priority 2: Bulk Price
      else if (bulkPrice.length > 0) {
        const leastPriceObject = bulkPrice.reduce((min, current) => {
          const currentEffectivePrice =
            +current.egg_price > 0
              ? +current.egg_price
              : +current.eggless_price || Infinity;
          const minEffectivePrice =
            +min.egg_price > 0
              ? +min.egg_price
              : +min.eggless_price || Infinity;
          return currentEffectivePrice < minEffectivePrice ? current : min;
        }, bulkPrice[0]);

        discountPrice =
          +leastPriceObject.egg_price > 0
            ? +leastPriceObject.egg_price
            : +leastPriceObject.eggless_price;

        originalPrice =
          +variation.egg > 0 ? +variation.egg : +variation.eggLess;

        if (originalPrice > 0 && originalPrice > discountPrice) {
          discountPercent = Math.round(
            ((originalPrice - discountPrice) / originalPrice) * 100,
          );
        }
      }
      // Priority 3: Customer / Product offer
      else {
        originalPrice =
          +variation.egg > 0 ? +variation.egg : +variation.eggLess;

        const customerOffer = +item?.customer_offer_price || 0;
        const productOffer = +item?.product_offer_price || 0;
        const offerPrices = [];

        if (customerOffer > 0 && customerOffer < originalPrice)
          offerPrices.push(customerOffer);
        if (productOffer > 0 && productOffer < originalPrice)
          offerPrices.push(productOffer);

        if (offerPrices.length > 0) {
          discountPrice = Math.min(...offerPrices);
          discountPercent = Math.round(
            ((originalPrice - discountPrice) / originalPrice) * 100,
          );
        } else {
          discountPrice =
            +variation.eggOriginalPrice > 0
              ? +variation.eggOriginalPrice
              : +variation.eggLessOriginalPrice;
          discountPercent = variation?.discountPercent || 0;
        }
      }

      return { discountPrice, originalPrice, discountPercent, comboPrice };
    }, [item]);

  const isDiscounted = originalPrice > discountPrice;
  const productTag =
    item?.customizable === 1 || item?.is_customizable === 1
      ? "Customisable"
      : item?.is_best_seller === 1
        ? "Featured"
        : "Curated";

  return (
    <CakeCard
      onClick={() =>
        navigate(`/product-description/${item?.product_name}/${item?.id}`)
      }
    >
      <CardContent>
        <Box sx={{ position: "relative", width: "100%", p: 1.25, pb: 0 }}>
          <ProductImageWrapper>
            <CakeImage
              src={item.app_image?.app_product_image}
              alt={item.product_name}
              loading="lazy"
            />
          </ProductImageWrapper>

          <ProductTag>{productTag}</ProductTag>

          {(item?.is_best_seller === 1 ||
            item?.is_customizable === 1 ||
            item?.is_featured === 1) && (
            <DeliveryBadge>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontWeight: 700,
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.88)",
                    boxShadow: "0 0 0 2px rgba(255,255,255,0.18)",
                  }}
                />
                Curated
              </Box>
            </DeliveryBadge>
          )}
        </Box>

        <CakeInfo>
          <CakeName>{item.product_name}</CakeName>
          <Ratings value={item?.ratings} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
              mt: 0.75,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
              {discountPrice > 0 && <Price>₹{discountPrice}</Price>}

              {isDiscounted &&
                originalPrice > 0 &&
                (comboPrice === 0 ||
                  customer?.customer_discount_id !== 0 ||
                  customer?.customer_discont !== null) && (
                  <OriginalPrice
                    sx={{
                      textDecoration: "line-through",
                      color: "var(--neutral-500)",
                    }}
                  >
                    ₹{originalPrice}
                  </OriginalPrice>
                )}
            </Box>

            {discountPercent > 0 &&
              !(
                comboPrice > 0 &&
                (!customer?.customer_discount_id ||
                  customer?.customer_discount_id === 0) &&
                !customer?.customer_discont
              ) && <SavePercentage>{discountPercent}% off</SavePercentage>}
          </Box>
        </CakeInfo>
      </CardContent>
    </CakeCard>
  );
});

/* ================= Styled Components ================= */

const Section = styled("section")(({ theme }) => ({
  padding: "10px 20px 24px 20px",
  margin: "0 auto",
  maxWidth: "100%",
  marginBottom: "10px",
  [theme.breakpoints.down("md")]: {
    padding: "10px",
  },
}));

export const SectionHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: "16px",
  [theme.breakpoints.down("md")]: {
    marginBottom: "8px",
  },
}));

export const SectionTitle = styled("h2")(({ theme }) => ({
  fontWeight: 700,
  lineHeight: "133%",
  color: "var(--neutral-black)",
  textAlign: "left",
  marginBottom: "20px",
  fontSize: "24px",
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    fontSize: "22px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
    marginBottom: "12px",
  },
}));

const SliderWrapper = styled("div")(() => ({
  ".slick-slide": {
    padding: "0 10px",
  },
  ".slick-list": {
    margin: "0 -10px",
  },
}));

const CakeCard = styled("div")(({ theme }) => ({
  cursor: "pointer",
  border: "1px solid #EEE7D8",
  borderRadius: "18px",
  background: "#FFFFFF",
  overflow: "hidden",
  padding: "0",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  boxShadow: "0 12px 30px rgba(35, 32, 28, 0.05)",
  transition: "box-shadow 0.25s ease, transform 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    boxShadow: "0 18px 40px rgba(183, 123, 26, 0.12)",
    borderColor: "#E6C77B",
    transform: "translateY(-3px)",
  },
  [theme.breakpoints.down("md")]: { width: "100%" },
  [theme.breakpoints.down("sm")]: { width: "100%" },
}));

const CardContent = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: "0",
  width: "100%",
}));

const ProductImageWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  background: "linear-gradient(180deg, #F9F6F1 0%, #F4EFE6 100%)",
  borderRadius: "14px",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "12px",
  },
}));

const CakeImage = styled("img")(({ theme }) => ({
  width: "100%",
  height: "240px",
  objectFit: "contain",
  background: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(245,236,220,0.5) 100%)",
  padding: "16px 16px 0 16px",
  [theme.breakpoints.down("md")]: { height: "180px" },
  [theme.breakpoints.down("sm")]: { height: "150px", padding: "12px 12px 0 12px" },
}));

const ProductTag = styled("span")(({ theme }) => ({
  position: "absolute",
  top: "12px",
  left: "12px",
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 10px",
  borderRadius: "999px",
  background: "rgba(255,255,255,0.94)",
  border: "1px solid rgba(183,123,26,0.2)",
  color: "#6B4A05",
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  zIndex: 1,
  boxShadow: "0 8px 18px rgba(39, 32, 25, 0.04)",
  [theme.breakpoints.down("sm")]: {
    fontSize: "9px",
    top: "10px",
    left: "10px",
  },
}));

const DeliveryBadge = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  background: "linear-gradient(135deg, #C39942, #A77416)",
  color: "white",
  fontWeight: 700,
  padding: "5px 10px",
  borderRadius: "0 10px 0 0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2,
  fontSize: "10px",
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
  boxShadow: "0 -6px 18px rgba(183, 123, 26, 0.18)",
  [theme.breakpoints.down("sm")]: {
    fontSize: "9px",
    padding: "4px 8px",
  },
}));

const CakeInfo = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "6px",
  width: "100%",
  padding: "14px 14px 16px",
}));

const CakeName = styled("h3")(({ theme }) => ({
  margin: "0",
  fontWeight: 700,
  fontSize: "17px",
  lineHeight: 1.4,
  color: "var(--neutral-800)",
  textAlign: "left",
  textTransform: "capitalize",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  minHeight: "24px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "15px",
    minHeight: "22px",
  },
}));

export const Tag = styled("span")(({ theme }) => ({
  fontWeight: "500",
  fontSize: "14px",
  padding: "4px 12px",
  borderRadius: "143px",
  background: "var(--primary-red-100)",
  width: "fit-content",
  lineHeight: "125%",
  color: "var(--primary-red-300)",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    padding: "2px 8px",
  },
}));

const Price = styled(Typography)(({ theme }) => ({
  fontSize: "18px",
  fontWeight: 800,
  color: "#1F2522",
  margin: 0,
  letterSpacing: "-0.02em",
  [theme.breakpoints.down("sm")]: { fontSize: "16px" },
}));

const OriginalPrice = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 500,
  margin: 0,
  [theme.breakpoints.down("sm")]: { fontSize: "12px" },
}));

const SavePercentage = styled(Typography)(({ theme }) => ({
  fontSize: "11px",
  fontWeight: 700,
  margin: 0,
  padding: "5px 8px",
  borderRadius: "999px",
  color: "#7A4F00",
  border: "1px solid rgba(183,123,26,0.25)",
  backgroundColor: "#FFF8EC",
  [theme.breakpoints.down("sm")]: { fontSize: "10px" },
}));

export default PopularCakes;
